import Numeral from 'numeral';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { range, union } from 'underscore';

import { Input } from '@eika/input';

import { changeSoknadField, verifySoknadField } from '../../../actions';
import { NameValueString, NameValueStringEvent, StringKeyValue } from '../../../actions/types';
import { AppState } from '../../../reducers';
import { ApplicationReducer } from '../../../reducers/application';
import { CardsReducer } from '../../../reducers/cards';
import { LoaderReducer } from '../../../reducers/loader';
import { UserReducer } from '../../../reducers/user';
import { trackEvent, TrackingStep } from '../../../util/piwik-utils';
import ValidationFields from '../../../util/validation/ValidationFields';
import StepSelect from '../../Common/StepSelect';
import ErrorMessage from '../../Messages/ErrorMessage';
import StepButtons from '../../Steps/StepButtons';
import AboutCreditCard from '../AboutCreditCard';

interface Props {
    action: {
        changeSoknadField: NameValueStringEvent;
        verifySoknadField: NameValueStringEvent;
    };
    onContactNextClick: (amountEnabled: boolean) => void;
    application: ApplicationReducer;
    cards: CardsReducer;
    text: StringKeyValue;
    loader: LoaderReducer;
    user: UserReducer;
}

const ContactInfo = (props: Props) => {
    Numeral.locale('no');
    const { text, application, cards, onContactNextClick, user, loader, action } = props;
    const { errors } = application;
    const { cardNameGold, cardNameBusiness } = text;
    const { korttype, supportedCards } = cards;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [loader.hasFetchedConsent]);

    const onChangeCreditButtonGroup = (e: NameValueString) => {
        action.changeSoknadField(e.name, e.value);
        action.verifySoknadField(e.name, e.value);
    };

    const onBlur = (e: any) => {
        const { name, value } = e.target;
        setTimeout(() => action.verifySoknadField(name, value), 250);
    };

    const onChange = (e: any) => {
        const { name, value } = e.target;
        action.changeSoknadField(
            name,
            value
                .trim()
                .replace('æ', '')
                .replace('ø', '')
                .replace('å', '')
                .replace('Æ', '')
                .replace('Ø', '')
                .replace('Å', ''),
        );
    };

    const userIsYoung = () => {
        const today = new Date();
        const birthDate = new Date(user.birthDate);

        const currentMilli = today.getTime();
        const birthMilli = birthDate.getTime();

        const twentyNineYears = 915151608000;
        const eighteenYears = 568025136000;

        return currentMilli - birthMilli <= twentyNineYears && currentMilli - birthMilli > eighteenYears;
    };

    let currentCard;
    let cardLabel;

    if (korttype === supportedCards.business) {
        cardLabel = cardNameBusiness;
        currentCard = cards.businessCard;
    } else if (korttype === supportedCards.gold) {
        cardLabel = cardNameGold;
        currentCard = cards.goldCard;
    }

    const mediumCredit = 30000;
    const maxCreditLimit = 210000;
    let stepper = 5000;
    let addon = 2500;

    if (currentCard?.kredittgrense > mediumCredit) {
        stepper = 10000;
        addon = 5000;
    }
    const minCreditLimit = currentCard?.kredittgrense
        ? Math.ceil(Math.round((currentCard.kredittgrense + addon) / stepper) * stepper) || 5000
        : 5000;

    const myRange = union(range(minCreditLimit, mediumCredit, 5000), range(mediumCredit, maxCreditLimit, 10000));
    const creditRange = myRange.map((value) => ({
        text: Numeral(value).format('0,0'),
        value,
    }));
    let defaultCreditIndex = myRange.indexOf(Number(text.creditAmountDefaultValue.trim()) || 50000);

    if (userIsYoung()) {
        defaultCreditIndex = myRange.indexOf(Number(text.creditAmountDefaultValueYoung.trim()) || 25000);
    }
    if (defaultCreditIndex === -1) defaultCreditIndex = 0;

    useEffect(() => {
        if (application.requestedCreditAmount === '') {
            action.changeSoknadField('requestedCreditAmount', creditRange[defaultCreditIndex]?.value?.toString());
        }
    }, [action, application.requestedCreditAmount, creditRange, defaultCreditIndex]);

    return (
        <div>
            {((cards.blockedBusinessCard && korttype === supportedCards.business) ||
                (cards.blockedGoldCard && korttype === supportedCards.gold)) && (
                <ErrorMessage title={text.accountStatusInvalidTitle} textList={[text.accountStatusInvalidText]} />
            )}

            {!cards.blockedGoldCard && !cards.blockedBusinessCard && (
                <div className="contact-info">
                    {currentCard?.kredittgrense ? (
                        <h3 className="credit-card-application-current-limit">
                            {text.currentCreditAmountTitle}
                            {Numeral(currentCard?.kredittgrense).format('0,0')}
                            {} kr
                        </h3>
                    ) : null}
                    {text.creditAmountInputLabel && <strong>{text.creditAmountInputLabel}</strong>}
                    <div className="credit-card-application-input">
                        <StepSelect
                            name={ValidationFields.CREDITAMOUNT}
                            label={cardLabel}
                            values={creditRange}
                            defaultIndex={defaultCreditIndex}
                            onChange={onChangeCreditButtonGroup}
                        />
                    </div>
                    {text.contactInfoTitle && (
                        <h2 className="credit-card-application-form-title">{text.contactInfoTitle}</h2>
                    )}
                    <div className="credit-card-application-input">
                        <Input
                            name={ValidationFields.EPOSTADRESSE}
                            type="email"
                            label={text.formEmailLabel}
                            value={application.epost}
                            error={errors.epost}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="credit-card-application-input">
                        <Input
                            name={ValidationFields.TELEFONNUMMER}
                            type="tel"
                            label={text.formPhoneLabel}
                            value={application.mobiltelefon}
                            error={errors.mobiltelefon}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    </div>
                    <StepButtons
                        showNext
                        nextChangeEvent={() => {
                            trackEvent(TrackingStep.ContactInfo);
                            onContactNextClick(user.testUser);
                        }}
                    />
                    <AboutCreditCard />
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    application: state.application,
    loader: state.loader,
    cards: state.cards,
    user: state.user,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            verifySoknadField,
            changeSoknadField,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContactInfo);
